import { jsPDF } from "jspdf";
import moment from "moment-timezone";

import envConfig from "@/config/envConfig";
import { EditionType, NewspaperType } from "@/types/epaper";

import { getImageUrl } from "./getImageUrl";
import { epaperPublicPath, isEpaperSubdomainHost } from "./epaperPaths";

export const EPAPER_EDITION_GROUPS = [
  { type: "newspaper", label: "Newspapers" },
  { type: "supplement", label: "Supplements" },
  { type: "magazine", label: "Magazines" },
  { type: "special-issue", label: "Special Issues" },
] as const;

export type EpaperEditionGroupType = (typeof EPAPER_EDITION_GROUPS)[number]["type"];

export function groupEditionsByType(editions: EditionType[]) {
  return EPAPER_EDITION_GROUPS.map(group => ({
    ...group,
    editions: editions.filter(edition => edition.editionType === group.type),
  })).filter(group => group.editions.length > 0);
}

const download = (url: string, filename: string) => {
  const element = document.createElement("a");
  element.setAttribute("href", url);
  element.setAttribute("download", filename);
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

type EpaperMediaRef = {
  url?: string;
  filename?: string;
  sizes?: {
    hd?: { filename?: string; url?: string } | null;
    ld?: { filename?: string; url?: string } | null;
  };
} | null | undefined;

function firstMediaSource(...candidates: Array<string | undefined | null>): string {
  for (const candidate of candidates) {
    const value = (candidate || "").trim();
    if (value) return value;
  }
  return "";
}

/**
 * File key or URL for an e-paper cut.
 * Prefer the original upload so Telugu newsprint stays sharp. HD/LD are JPEG
 * recompressions, and HD is often omitted for small clips so the old fallback
 * was the 360px LD derivative.
 */
export function getEpaperClipAssetSource(media: EpaperMediaRef): string {
  if (!media) return "";
  return firstMediaSource(
    media.filename,
    media.url,
    media.sizes?.hd?.filename,
    media.sizes?.hd?.url,
    media.sizes?.ld?.filename,
    media.sizes?.ld?.url,
  );
}

/** Page image source. Use `original` when cropping so clips are not taken from the HD JPEG. */
export function getEpaperPageAssetSource(
  media: EpaperMediaRef,
  quality: "original" | "hd" | "ld" = "hd",
): string {
  if (!media) return "";
  if (quality === "original") {
    return firstMediaSource(
      media.filename,
      media.url,
      media.sizes?.hd?.filename,
      media.sizes?.ld?.filename,
    );
  }
  if (quality === "ld") {
    return firstMediaSource(
      media.sizes?.ld?.filename,
      media.sizes?.hd?.filename,
      media.filename,
      media.url,
    );
  }
  return firstMediaSource(
    media.sizes?.hd?.filename,
    media.filename,
    media.url,
    media.sizes?.ld?.filename,
  );
}

/** Fetch from current origin when media lives on API/CDN (avoids CORS on `fetch`; `<img>` is unaffected). */
export function sameOriginMediaFetchUrl(resolvedHref: string): string {
  if (typeof window === "undefined") return resolvedHref;
  try {
    const abs = /^https?:\/\//i.test(resolvedHref)
      ? resolvedHref
      : new URL(`/${resolvedHref.replace(/^\/+/, "")}`, window.location.origin).href;
    const target = new URL(abs);
    if (target.origin === window.location.origin) return abs;
    return `${window.location.origin}/api/image-proxy?url=${encodeURIComponent(abs)}`;
  } catch {
    return resolvedHref;
  }
}

export const handleEpaperClipDownload = async (source: string) => {
  const raw = (source || "").trim();
  if (!raw) {
    throw new Error("EMPTY_SOURCE");
  }

  // Blob URLs from the crop preview can be downloaded directly (session-only).
  if (raw.startsWith("blob:")) {
    download(raw, "epaper-cut.png");
    return;
  }

  // An absolute preview URL is already the file (or the same-origin image proxy).
  // Running it through getImageUrl again rewrites /api/image-proxy?url=... into a
  // missing storage key, and Chrome reports that 403 as "No permissions".
  const resolved = (/^https?:\/\//i.test(raw) ? raw : getImageUrl(raw) || raw).trim();
  if (!resolved) {
    throw new Error("EMPTY_RESOLVED_URL");
  }

  const fetchUrl = sameOriginMediaFetchUrl(resolved);
  const response = await fetch(fetchUrl);
  if (!response.ok) {
    throw new Error("DOWNLOAD_FAILED");
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.startsWith("image/")) {
    throw new Error("DOWNLOAD_FAILED");
  }

  const blob = await response.blob();
  const ext = contentType.includes("png")
    ? "png"
    : contentType.includes("webp")
      ? "webp"
      : "jpg";
  const objectUrl = URL.createObjectURL(blob);
  try {
    download(objectUrl, `epaper-cut.${ext}`);
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  }
};

/** Visual height of the stacked Faircode/copyright bar on mobile reader (must match footer + safe area offset). */
export const MOBILE_EPAPER_BRAND_FOOTER_HEIGHT = "4rem";

const EPAPER_PUBLICATION_TYPES = new Set<string>(EPAPER_EDITION_GROUPS.map(group => group.type));

/** True on `/epaper` or `/[lang]/epaper` (e-paper home / editions list). */
export function isEpaperIndexRoute(pathname: string) {
  const parts = pathname.split("/").filter(Boolean);
  const i = parts.indexOf("epaper");
  if (i >= 0 && parts.length === i + 1) return true;
  // epaper.prajasakti.com/ is the editions list; the /epaper segment is not in the URL.
  return parts.length === 0;
}

/** True on `/[lang]/epaper/[type]/[edition]/[date]` and the subdomain form `/[type]/[edition]/[date]`. */
export function isEpaperReaderRoute(pathname: string) {
  const parts = pathname.split("/").filter(Boolean);
  const i = parts.indexOf("epaper");
  if (i >= 0 && parts.length >= i + 4) return true;
  const start = parts[0] && /^[a-z]{2}-[A-Z]{2}$/.test(parts[0]) ? 1 : 0;
  return parts.length >= start + 3 && EPAPER_PUBLICATION_TYPES.has(parts[start]);
}

export function buildEpaperPdfFilename(
  date: Date | string,
  editionSlug: string | undefined,
  pageNumber?: number,
): string {
  const base = `${moment(date).tz("Asia/Kolkata").format("DDMMYYYY")}-${editionSlug || "epaper"}`;
  return pageNumber != null ? `${base}-${pageNumber}.pdf` : `${base}.pdf`;
}

function isBackendHost(url: string): boolean {
  try {
    const target = new URL(url);
    const host = target.hostname.toLowerCase();
    if (host.includes("backenddev") || host.includes("backend.") || host.startsWith("admin.")) {
      return true;
    }
    const apiBase = (envConfig.publicServerUrl || "").replace(/\/+$/, "");
    if (apiBase) {
      // Compare full origin (includes port) so localhost:3005 frontend ≠ localhost:3000 API.
      const apiOrigin = new URL(apiBase).origin;
      if (target.origin === apiOrigin) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Origin for epaper share/copy links — always the frontend site, never the API host. */
export function getEpaperShareOrigin(): string {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  const client = (envConfig.clientUrl || "").replace(/\/+$/, "");
  if (client && !isBackendHost(client)) return client;
  return "";
}

/** Canonical locale-less e-paper reader URL for copy/share (middleware rewrites to /te-IN/). */
export function buildEpaperReaderShareUrl(
  params: {
    publicationType?: string;
    edition?: string;
    date?: string | Date;
    page?: number;
  },
  origin?: string,
): string {
  const publicationType = params.publicationType?.trim();
  const edition = params.edition?.trim();
  const dateStr = params.date
    ? moment(params.date).tz("Asia/Kolkata").format("YYYY-MM-DD")
    : "";
  if (!publicationType || !edition || !dateStr) return "";

  const page = params.page && params.page > 0 ? params.page : 1;
  const base = (origin || getEpaperShareOrigin()).replace(/\/+$/, "");
  if (!base) return "";
  let host = "";
  try {
    host = new URL(base).host;
  } catch {
    host = "";
  }
  const path = epaperPublicPath(
    `/epaper/${publicationType}/${edition}/${dateStr}`,
    isEpaperSubdomainHost(host),
  );
  return `${base}${path}?page=${page}&type=fullview`;
}

function isStoredEpaperMediaKey(key: string): boolean {
  return (
    key.startsWith("gen-epaper/") ||
    key.startsWith("epaper/") ||
    key.startsWith("epaper-media/")
  );
}

/** Public URL for the full-edition PDF (CDN or download proxy). */
export function getFullEpaperPdfLink(epaperData: NewspaperType): string | null {
  const pdfSource = (epaperData.pdfUrl || "").trim();
  if (!pdfSource) return null;

  const isPdf = pdfSource.split("?")[0]?.toLowerCase()?.endsWith(".pdf");
  if (!isPdf) return null;

  const downloadUrl = getEpaperPdfDownloadUrl(pdfSource);
  return downloadUrl || null;
}

/** Copy the full epaper PDF link to the clipboard. */
export async function copyFullEpaperPdfLink(epaperData: NewspaperType): Promise<void> {
  const link = getFullEpaperPdfLink(epaperData);
  if (!link) throw new Error("FULL_PDF_UNAVAILABLE");
  await navigator.clipboard.writeText(link);
}

/** Resolve stored PDF key or URL to a fetchable URL. */
export function resolveEpaperPdfUrl(pdfUrlOrKey: string): string {
  const raw = (pdfUrlOrKey || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const key = raw.replace(/^\/+/, "");
  const imgBase = (envConfig.imgStoreUrl || "").replace(/\/+$/, "");
  return imgBase ? `${imgBase}/${key}` : getImageUrl(key);
}

function getFrontendOrigin(): string {
  return getEpaperShareOrigin();
}

/** Frontend-origin proxy for private epaper files (shareable frontdev URL, not backend API). */
function getFrontendEpaperDownloadUrl(key: string, downloadName?: string): string {
  const qs = new URLSearchParams({ filename: key });
  if (downloadName) qs.set("name", downloadName);
  const origin = getFrontendOrigin();
  if (origin) {
    return `${origin}/api/epaper-download?${qs.toString()}`;
  }
  return `${envConfig.publicServerUrl}/api/v1/epapers/download-page?${qs.toString()}`;
}

/**
 * Extract an S3/storage key from a raw key or Payload `/api/epaper-media/file/<key>` URL
 * so downloads can go through the attachment proxy (not image-proxy / inline view).
 */
function extractEpaperStorageKey(raw: string): string | null {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;

  const asKey = trimmed.replace(/^\/+/, "");
  if (isStoredEpaperMediaKey(asKey) && !/^https?:/i.test(trimmed)) {
    return asKey;
  }

  try {
    const url = new URL(trimmed);
    const match = url.pathname.match(/\/(?:api\/)?epaper-media\/file\/(.+)$/i);
    if (match?.[1]) {
      const key = decodeURIComponent(match[1]).replace(/^\/+/, "");
      if (isStoredEpaperMediaKey(key)) return key;
    }
  } catch {
    // not a URL
  }

  return null;
}

/** Stable server URL for downloading epaper media (never a blob URL). */
export function getEpaperMediaDownloadUrl(filenameOrKey: string, downloadName?: string): string {
  const raw = (filenameOrKey || "").trim();
  if (!raw) return "";

  const storageKey = extractEpaperStorageKey(raw);
  if (storageKey) {
    // Page + full PDFs: same-origin attachment proxy so mobile saves to Downloads.
    return getFrontendEpaperDownloadUrl(storageKey, downloadName);
  }

  if (/^https?:\/\//i.test(raw)) {
    return sameOriginMediaFetchUrl(raw);
  }

  const key = raw.replace(/^\/+/, "");
  const cdnUrl = resolveEpaperPdfUrl(raw);
  if (cdnUrl && isStoredEpaperMediaKey(key)) {
    return sameOriginMediaFetchUrl(cdnUrl);
  }

  return sameOriginMediaFetchUrl(cdnUrl);
}

/** Stable server URL for downloading a stored or remote epaper PDF (never a blob URL). */
export function getEpaperPdfDownloadUrl(pdfUrlOrKey: string, downloadName?: string): string {
  return getEpaperMediaDownloadUrl(pdfUrlOrKey, downloadName);
}

async function isPdfBlob(blob: Blob): Promise<boolean> {
  const header = await blob.slice(0, 5).text();
  return header.startsWith("%PDF");
}

function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/** Open a stable https URL so mobile Safari share uses a real link, not blob:. */
function openStableDocumentUrl(url: string): void {
  if (isMobileBrowser()) {
    window.location.assign(url);
    return;
  }
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    window.location.assign(url);
  }
}

/**
 * Trigger a file download from a same-origin URL that sends Content-Disposition: attachment.
 * Prefer this on mobile — blob: downloads often open inline and never reach Downloads/Files.
 */
function triggerAttachmentDownload(url: string, filename: string): void {
  // iOS Safari often ignores <a download>, even for same-origin URLs. A hidden iframe
  // pointed at an attachment response saves to Files/Downloads without leaving the reader.
  if (isMobileBrowser() && !url.startsWith("blob:")) {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.tabIndex = -1;
    iframe.style.cssText = "position:fixed;width:0;height:0;border:0;visibility:hidden";
    iframe.src = url;
    document.body.appendChild(iframe);
    setTimeout(() => iframe.remove(), 60_000);
    return;
  }

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.setAttribute("download", filename);
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

/** Fetch a remote PDF, validate it, then save locally (blob is revoked immediately after). */
async function downloadValidatedPdfFile(url: string, filename: string): Promise<void> {
  // Mobile: do not buffer into a blob — iOS/Android often ignore <a download> on blob:
  // URLs and never put the file in Downloads. Hit the attachment proxy instead.
  // Important: trigger the click synchronously (no await before it) so iOS keeps the
  // user-gesture and actually starts the download.
  if (isMobileBrowser()) {
    if (!url || url.startsWith("blob:")) {
      throw new Error("PDF_DOWNLOAD_UNAVAILABLE");
    }
    const openUrl = isBackendHost(url) ? sameOriginMediaFetchUrl(url) : url;
    triggerAttachmentDownload(openUrl, filename);
    return;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`PDF_DOWNLOAD_FAILED_${response.status}`);
  }

  const blob = await response.blob();
  if (!(await isPdfBlob(blob))) {
    throw new Error("INVALID_PDF_CONTENT");
  }

  const blobUrl = URL.createObjectURL(blob);
  try {
    triggerAttachmentDownload(blobUrl, filename);
  } finally {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

function isEpaperPdfSource(pdfUrlOrKey: string): boolean {
  const path = (pdfUrlOrKey || "").split("?")[0] || "";
  const lower = path.toLowerCase();
  if (lower.endsWith(".pdf")) return true;
  // Converted page PDFs are stored as `epaper/...pdf-<uuid>` (no trailing .pdf).
  if (/\.pdf($|[^a-z0-9])/i.test(path)) return true;
  return /\/epaper(%2F|\/)/i.test(path) && /\.pdf/i.test(path);
}

/** Download the currently viewed page as PDF. */

/** Original upload first: HD/LD are recompressed derivatives and look blurry in print. */
function getEpaperPdfImageFilename(media: EpaperMediaRef): string {
  return firstMediaSource(
    media?.filename,
    media?.sizes?.hd?.filename,
    media?.sizes?.ld?.filename,
  );
}

/** Add an image to the current jsPDF page, fitted + centred on A4 (no stretching), no re-compression. */
function addImageFitted(pdf: jsPDF, dataUrl: string, format: "PNG" | "JPEG"): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const scale = Math.min(pageW / img.naturalWidth, pageH / img.naturalHeight);
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      pdf.addImage(dataUrl, format, (pageW - w) / 2, (pageH - h) / 2, w, h, undefined, "NONE");
      resolve();
    };
    img.onerror = () => reject(new Error("PAGE_IMAGE_UNAVAILABLE"));
    img.src = dataUrl;
  });
}

export async function downloadEpaperCurrentPage(
  epaperData: NewspaperType,
  activePageIndex: number,
): Promise<void> {
  const activePage = epaperData.pages[activePageIndex];
  const pdfUrl = (activePage?.pdfUrl || "").trim();
  const saveAs = buildEpaperPdfFilename(
    epaperData.date,
    epaperData.editionId?.slug,
    activePageIndex + 1,
  );

  if (pdfUrl && isEpaperPdfSource(pdfUrl)) {
    const downloadUrl = getEpaperPdfDownloadUrl(pdfUrl, saveAs);
    if (!downloadUrl) throw new Error("PAGE_PDF_UNAVAILABLE");
    await downloadValidatedPdfFile(downloadUrl, saveAs);
    return;
  }

  const filename = getEpaperPdfImageFilename(activePage?.page);
  if (!filename) throw new Error("PAGE_IMAGE_UNAVAILABLE");

  const downloadUrl = getEpaperMediaDownloadUrl(filename, saveAs);

  // Mobile: cannot assemble jsPDF reliably; save the page image via attachment proxy.
  if (isMobileBrowser()) {
    if (!downloadUrl || downloadUrl.startsWith("blob:")) {
      throw new Error("PDF_DOWNLOAD_UNAVAILABLE");
    }
    const openUrl = isBackendHost(downloadUrl)
      ? sameOriginMediaFetchUrl(downloadUrl)
      : downloadUrl;
    triggerAttachmentDownload(openUrl, saveAs.replace(/\.pdf$/i, ".jpg"));
    return;
  }

  const response = await fetch(downloadUrl);
  if (!response.ok) throw new Error(response.statusText);
  const blob = await response.blob();

  const base64data = await blobToDataUrl(blob);
  const lower = filename.toLowerCase();
  const format = lower.endsWith(".png") ? "PNG" : "JPEG";
  const pdf = new jsPDF({ compress: false });
  await addImageFitted(pdf, base64data, format);
  pdf.save(saveAs);
}

/** Build a multi-page PDF from all page images when the stored full PDF is missing. */
async function downloadEpaperFromAllPageImages(epaperData: NewspaperType): Promise<void> {
  const pages = epaperData.pages ?? [];
  if (pages.length === 0) throw new Error("FULL_PDF_UNAVAILABLE");

  if (isMobileBrowser()) {
    throw new Error("FULL_PDF_UNAVAILABLE");
  }

  const pdf = new jsPDF({ compress: false });
  let pageAdded = false;

  for (const page of pages) {
    const filename = getEpaperPdfImageFilename(page?.page);
    if (!filename) continue;

    const downloadUrl = getEpaperMediaDownloadUrl(filename);
    const response = await fetch(downloadUrl);
    if (!response.ok) continue;

    const blob = await response.blob();
    const base64data = await blobToDataUrl(blob);
    const lower = filename.toLowerCase();
    const format = lower.endsWith(".png") ? "PNG" : "JPEG";

    if (pageAdded) pdf.addPage();
    await addImageFitted(pdf, base64data, format);
    pageAdded = true;
  }

  if (!pageAdded) throw new Error("FULL_PDF_UNAVAILABLE");

  pdf.save(buildEpaperPdfFilename(epaperData.date, epaperData.editionId?.slug));
}

/** Download the full epaper PDF (all pages). */
export async function downloadFullEpaperPdf(epaperData: NewspaperType): Promise<void> {
  const pdfSource = (epaperData.pdfUrl || "").trim();
  const isPdf = pdfSource.split("?")[0]?.toLowerCase()?.endsWith(".pdf");
  const filename = buildEpaperPdfFilename(epaperData.date, epaperData.editionId?.slug);

  // Always download through the same-origin proxy — the backend streams the PDF, so the
  // browser never navigates to (or shows) a DigitalOcean Spaces URL.
  if (pdfSource && isPdf) {
    const downloadUrl =
      getEpaperPdfDownloadUrl(pdfSource, filename) || getFullEpaperPdfLink(epaperData);
    if (downloadUrl) {
      try {
        await downloadValidatedPdfFile(downloadUrl, filename);
        return;
      } catch (err) {
        console.warn("Full epaper PDF fetch failed, falling back to page images:", err);
      }
    }
  }

  await downloadEpaperFromAllPageImages(epaperData);
}
